import { useCallback, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { Botao } from '../../../components/Botao'
import { EtiquetaDePrioridade, EtiquetaDeRisco } from '../../../components/Etiqueta'
import { Explicacao } from '../../../components/Explicacao'
import { cn } from '../../../lib/cn'
import { data, dataHoraPorExtenso } from '../../../lib/datas'
import type { ResumoGmud } from '../../../types/gmud'
import { SeloDeBloqueio, ValorOuVazio } from '../lista/marcadores'
import { AcoesDoCartao } from './AcoesDoCartao'
import { iniciarArraste, type LigacoesDoArraste } from './arraste'
import { QUADRO } from './textosDoQuadro'

interface Props {
  gmud: ResumoGmud
  ligacoes: LigacoesDoArraste
}

/**
 * Uma solicitação no quadro.
 *
 * **`CartaoDeGmud` (da lista) não se reusa aqui, e não é descuido.** Aquele cartão é UM link, e o
 * comentário dele diz por quê: alvo de toque inteiro, um destino só anunciado, "abrir em nova aba"
 * funcionando — "por isso nada aqui dentro pode virar botão". Este cartão precisa de um botão
 * dentro. São invariantes opostas; o que se compartilha são os pedaços (`SeloDeBloqueio`,
 * `ValorOuVazio`, as etiquetas).
 *
 * **O teclado é o caminho primário, e o arraste é o atalho de mouse.** O botão "Mover…" abre a
 * mesma lista de `/transicoes-possiveis` que o arraste usa, cai nos mesmos diálogos e é o mesmo
 * código — paridade por construção, e não por um segundo caminho escrito para o teclado. Não há
 * modo de arraste por tecla: pegar com Espaço e mover com as setas é um modo invisível, e para
 * servidor leigo em TI isso não é acessibilidade, é uma segunda interface a aprender.
 *
 * @param props a solicitação e os fios do arraste
 * @returns o cartão
 */
export function CartaoDoQuadro({ gmud, ligacoes }: Props) {
  const [abertoPeloBotao, setAbertoPeloBotao] = useState(false)
  const idDoMenu = useId()
  const proposta = ligacoes.proposta?.gmudId === gmud.id ? ligacoes.proposta : null
  const aberto = abertoPeloBotao || proposta !== null

  const fechar = useCallback(() => {
    setAbertoPeloBotao(false)
    ligacoes.aoFecharProposta()
  }, [ligacoes])

  function alternar() {
    if (aberto) {
      fechar()
      return
    }
    setAbertoPeloBotao(true)
  }

  return (
    <li>
      <article
        draggable
        onDragStart={(evento) => {
          iniciarArraste(evento, gmud.id)
          ligacoes.aoPegar(gmud)
        }}
        onDragEnd={ligacoes.aoLargar}
        className={cn(
          'rounded-xl border border-slate-200 bg-white p-3 shadow-cartao',
          ligacoes.emArraste === gmud.id && 'opacity-60',
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <Link
            to={`/gmuds/${gmud.id}`}
            className={cn(
              'font-semibold text-niteroi-700 underline-offset-2 hover:underline',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-niteroi-600',
            )}
          >
            {gmud.codigo}
          </Link>
          <span className="flex items-center gap-1.5">
            {gmud.bloqueadoParaDeploy && <SeloDeBloqueio />}
            <time
              dateTime={gmud.criadoEm}
              title={dataHoraPorExtenso(gmud.criadoEm)}
              className="text-xs tabular-nums text-slate-500"
            >
              {data(gmud.criadoEm)}
            </time>
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <EtiquetaDePrioridade prioridade={gmud.prioridade} rotulo={gmud.prioridadeRotulo} />
          <EtiquetaDeRisco risco={gmud.riscoNivel} />
        </div>

        {/* `<dl>` e não linhas soltas: sem a marcação, "Chamado" e "2026051281" viram dois textos
            sem relação nenhuma para quem ouve a tela. */}
        <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-xs">
          <dt className="text-slate-500">Chamado</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={gmud.chamadoNumero} />
          </dd>
          <dt className="text-slate-500">Sistema</dt>
          <dd className="min-w-0 text-slate-800">
            <ValorOuVazio texto={gmud.sistema} />
          </dd>
        </dl>

        <div className="mt-3 flex items-center gap-1">
          <Botao tamanho="pequeno" aria-expanded={aberto} aria-controls={idDoMenu} onClick={alternar}>
            {QUADRO.mover}
          </Botao>
          <Explicacao titulo={QUADRO.mover} texto={QUADRO.moverAjuda} />
        </div>

        {aberto && (
          <div id={idDoMenu}>
            <AcoesDoCartao
              gmud={gmud}
              destinoProposto={proposta?.destino ?? null}
              nomeDaZona={proposta?.nomeDaZona ?? null}
              aoAnunciar={ligacoes.aoAnunciar}
              aoConcluir={fechar}
            />
          </div>
        )}
      </article>
    </li>
  )
}
